import React, { forwardRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { VARIANTS } from './utils';
import { badgeClasses as classes } from './badgeClasses';
import { BadgeRoot } from './Badge.style';

const Badge = forwardRef(
  (
    {
      badgeContent,
      positiveRange = [1, Infinity],
      negativeRange = [-1, -Infinity],
      color = 'primary',
      variant = 'notification',
      min = -99,
      max = 99, // this is MUI default
      showZero = false,
      onlyNumber = false,
      inline = false,
      ...rest
    },
    ref
  ) => {
    const contentToRender = VARIANTS[variant]({
      badgeContent,
      min,
      max,
      showZero,
      onlyNumber
    });

    return (
      <BadgeRoot
        ref={ref}
        max={max}
        classes={{
          badge: clsx(classes[variant], { [classes.inline]: inline })
        }}
        badgeContent={contentToRender}
        incomingBadgeContent={badgeContent}
        color={color}
        showZero={showZero}
        variant={variant}
        negativeRange={negativeRange}
        positiveRange={positiveRange}
        inline={inline}
        data-qa-id="badge"
        {...rest}
      />
    );
  }
);

Badge.displayName = 'Badge';

const range = (props, propName, componentName) => {
  if (props[propName]) {
    const range = props[propName];
    const invalidType = !Array.isArray(range) && range !== false;
    const notTuple = range.length !== 2;
    if (invalidType || notTuple) {
      return new Error(
        `${propName} in ${componentName} has invalid type and is not representing a range: Array[startRange<Number>], endRange<Number>]. If you wish to disable ${propName}, you can pass ${propName}={false}.`
      );
    }

    const invalidItemsType = !range.every(
      item => Number.isInteger(item) || Math.abs(item) === Infinity
    );
    if (invalidItemsType) {
      return new Error(
        `${propName} in ${componentName} has invalid items. ${propName} should be an array of 2 numbers [startRange, endRange].`
      );
    }
  }
  return null;
};

Badge.propTypes = {
  /** The content rendered within the badge. */
  badgeContent: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.node
  ]),
  /** The badge will be added relative to this node (except custom). */
  children: PropTypes.node,
  /** Min count to show. */
  min: PropTypes.number,
  /** Max count to show. */
  max: PropTypes.number,
  /** The variant to use. */
  variant: PropTypes.oneOf([
    'trend',
    'counter',
    'notification',
    'new',
    'premium',
    'beta',
    'coming-soon',
    'custom',
    'recommended'
  ]),
  /** If set to true, the Badge is positioned relative and not absolute */
  inline: PropTypes.bool,
  /** Set different color (when used as a counter) */
  color: PropTypes.oneOf(['primary', 'secondary']),
  /** Controls whether the badge is hidden when badgeContent is zero. */
  showZero: PropTypes.bool,
  /** If set to true, the Badge is only the number, without + or - (when used as a counter)*/
  onlyNumber: PropTypes.bool,
  /** The range of positive(**allow only for trend variant**) - array - [start, end]. to disable positiveRange please pass false */
  positiveRange: range,
  /** The range of negative(**allow only for trend variant**) - array - [start, end]. to disable negativeRange please pass false */
  negativeRange: range
};

Badge.classes = classes;

export default Badge;
